import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Eye } from 'lucide-react';

const EyebrowProcess = () => {
  const { treatmentProcess, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  // Only show on eyebrow transplant pages
  if (features?.analysisType !== 'eyebrow' || !treatmentProcess?.length) {
    return null;
  }

  // Process step translations
  const processTranslations = {
    'Planung': { en: 'Planning' },
    'Entnahme': { en: 'Extraction' },
    'Einpflanzung': { en: 'Implantation' },
    'Endergebnis': { en: 'Final Result' },
  };

  const descriptionTranslations = {
    'In der Planungsphase wird die Augenbrauenform sorgfältig entworfen und die Anzahl der benötigten Grafts bestimmt. Eine detaillierte Analyse Ihrer Spenderzonen und des Empfängerbereichs ermöglicht eine präzise und individuelle Planung.': {
      en: 'In the planning phase, the eyebrow shape is carefully designed and the number of grafts needed is determined. A detailed analysis of your donor zones and recipient area enables precise and individual planning.'
    },
    'Bei der Entnahme werden einzelne Haarfollikel aus der Spenderzone (meist am Hinterkopf) entnommen. Diese Follikeleinheiten werden sorgfältig unter dem Mikroskop vorbereitet, um eine hohe Überlebensrate zu gewährleisten.': {
      en: 'During extraction, individual hair follicles are removed from the donor zone (usually the back of the head). These follicular units are carefully prepared under the microscope to ensure a high survival rate.'
    },
    'Die vorbereiteten Haarfollikel werden präzise in die vorher festgelegten Empfängerzonen im Augenbrauenbereich eingesetzt. Dabei wird besonders auf den natürlichen Wuchswinkel und die Verteilung geachtet.': {
      en: 'The prepared hair follicles are precisely placed into the predetermined recipient zones in the eyebrow area. Special attention is paid to the natural growth angle and distribution.'
    },
    'Nach etwa 6-9 Monaten ist das endgültige Ergebnis sichtbar. Die transplantierten Haare wachsen natürlich und können wie Ihre eigenen Augenbrauen getrimmt und gestylt werden.': {
      en: 'After about 6-9 months, the final result is visible. The transplanted hairs grow naturally and can be trimmed and styled like your own eyebrows.'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return processTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };

  return (
    <section id="behandlungsprozess-augenbrauen" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20 text-[#5A8BA6]'
          }`}>
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">
              {language === 'de' ? 'Ihr Weg zu neuen Augenbrauen' : 'Your Path to New Eyebrows'}
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Der ' : 'The '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Behandlungsprozess' : 'Treatment Process'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Transparente Darstellung aller Schritte von der ersten Planung bis zum endgültigen Ergebnis Ihrer Augenbrauentransplantation'
              : 'Transparent presentation of all steps from initial planning to the final result of your eyebrow transplant'}
          </p>
        </div>

        {/* Process Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {treatmentProcess.map((step, index) => (
            <div
              key={step.id}
              className="group relative"
            >
              {/* Card */}
              <div className={`rounded-2xl overflow-hidden border transition-all duration-500 h-full card-lift ${
                isDark 
                  ? 'bg-[#0f0f0f] border-white/5 hover:border-[#7BA7C2]/30'
                  : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
              }`}>
                {/* Step Number */}
                <div className={`absolute top-4 left-4 z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  isDark ? 'bg-[#7BA7C2] text-white' : 'bg-[#5A8BA6] text-white'
                }`}>
                  {index + 1}
                </div>

                {/* Image */}
                <div className={`relative h-48 flex items-center justify-center p-6 ${
                  isDark ? 'bg-[#0a0a0a]' : 'bg-gray-100'
                }`}>
                  <img
                    src={step.image}
                    alt={step.title}
                    loading="lazy"
                    className={`w-full h-full object-contain max-h-36 transition-transform duration-500 group-hover:scale-110 ${
                      isDark ? 'filter brightness-90' : ''
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                    isDark 
                      ? 'text-white group-hover:text-[#7BA7C2]' 
                      : 'text-gray-900 group-hover:text-[#5A8BA6]'
                  }`}>
                    {getTranslatedTitle(step.title)}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getTranslatedDescription(step.description)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EyebrowProcess;
